import { api, _converse } from '@converse/headless';

/**
 * Returns the Jitsi server base URL.
 * Uses the 'meet_url' setting if set, otherwise derives it from the XMPP domain.
 * @returns {string|null}
 */
export function getMeetBaseUrl() {
    const explicit = api.settings.get('meet_url');
    if (explicit) return explicit.replace(/\/$/, '');

    const domain = _converse.session?.get('domain') || '';
    if (!domain) return null;
    const baseDomain = domain.replace(/^[^.]+\./, ''); // chat.x.y → x.y
    return `https://meet.${baseDomain}`;
}

/**
 * Resolves the current user's own display name for the Jitsi prejoin screen.
 * sessionStorage.s_fullname is only ever set by a fresh login (performLogin());
 * an Electron restart that auto-logs-in from persisted encrypted credentials
 * skips that call entirely, leaving it empty and falling through to a raw
 * JID/nick (e.g. "1100006") — the profile vcard is fetched on every connect
 * regardless of how login happened, so it's a reliable fallback.
 * @param {string} [fallback] - Used only if neither source resolves a name.
 * @returns {string}
 */
export function getOwnDisplayName(fallback = '') {
    return (
        sessionStorage.getItem('s_fullname') ||
        _converse.state.profile?.vcard?.get('fullname') ||
        _converse.state.profile?.vcard?.get('nickname') ||
        fallback
    );
}

const URL_REGEX = /https?:\/\/[^\s<>"']+/gi;

/**
 * Scans message body for a conference URL (matches meet base URL). Only
 * finds anything for messages sent before the "started video conference"
 * message stopped embedding the literal link (see isConferenceStartMessage /
 * getConferenceUrl) — kept for those older, already-sent messages.
 * @param {string} body
 * @returns {string|null} The full conference URL or null
 */
export function extractConferenceUrl(body) {
    if (!body) return null;
    const base = getMeetBaseUrl();
    if (!base) return null;
    for (const url of (body.match(URL_REGEX) || [])) {
        if (url.startsWith(base + '/')) return url;
    }
    return null;
}

// Stem shared by both the base ("...розпочинає...") and actor_self
// ("...розпочинаєте...", note the self-conjugation isn't a substring of the
// base form) variants groupevents.js renders for conference-started --
// matching only the base form previously meant the call's own initiator
// never got a join card on their own "started" line.
const CONFERENCE_START_MARKER = 'розпочина';
const CONFERENCE_END_MARKER = 'Відеоконференцію завершено';

/**
 * @param {string} body
 * @returns {boolean}
 */
export function isConferenceStartMessage(body) {
    return !!body && body.includes(CONFERENCE_START_MARKER) && body.includes('відеоконференцію');
}

/**
 * @param {string} body
 * @returns {boolean}
 */
export function isConferenceEndMessage(body) {
    return !!body && body.includes(CONFERENCE_END_MARKER);
}

/**
 * @param {string} body
 * @returns {boolean}
 */
export function isConferenceMessage(body) {
    return isConferenceStartMessage(body) || isConferenceEndMessage(body);
}

/**
 * Deterministically derives a room's Jitsi join URL from its own jid. Every
 * member already knows the room's jid, so this never needs to travel in the
 * message body — embedding the literal link there would leak a joinable URL
 * to anyone who can read the raw stanza/MAM history, not just this app's UI.
 * @param {string} mucJid
 * @returns {string|null}
 */
export function getConferenceUrl(mucJid) {
    const base = getMeetBaseUrl();
    if (!base || !mucJid) return null;
    const roomName = mucJid.split('@')[0];
    return `${base}/${roomName}`;
}
